import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { articlesAPI } from '../services/api';
import { validateAdminAccess } from '../utils/security';
import { useArticles } from '../context/ArticleContext';
import RichTextEditor from '../components/RichTextEditor';
import ImageUploader from '../components/ImageUploader';
import './AdminPanel.css';

function EditArticle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [article, setArticle] = useState({
    title: '',
    subtitle: '',
    content: '',
    author: '',
    category: 'Film',
    image: '',
    publishDate: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      if (validateAdminAccess()) {
        setIsAuthenticated(true);
      } else {
        navigate('/iffa-admin-login');
      }
    };
    
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated && id) {
      loadArticle();
    }
  }, [isAuthenticated, id]);

  const loadArticle = async () => {
    try {
      const articleData = await articlesAPI.getArticle(id);
      if (articleData) {
        setArticle({
          ...articleData,
          publishDate: articleData.publishDate || articleData.publishTime?.split('T')[0] || new Date().toISOString().split('T')[0]
        });
      }
    } catch (error) {
      alert('Error loading article: ' + error.message);
    } finally {
      setLoading(false);
    }
  };



  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      let finalImageUrl = article.image;
      
      // If image is a file object, upload it to S3 first
      if (typeof article.image === 'object' && article.image.file) {
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(article.image.file);
        });
        
        const uploadResponse = await fetch(`${process.env.REACT_APP_AWS_API_URL}/upload-image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            image: base64,
            fileName: article.image.fileName,
            contentType: article.image.contentType
          })
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image to S3');
        }
        
        const uploadResult = await uploadResponse.json();
        if (uploadResult.success) {
          finalImageUrl = uploadResult.imageUrl;
        } else {
          throw new Error('Image upload failed');
        }
      }
      
      const articleToUpdate = {
        ...article,
        content: formattedContent,
        image: finalImageUrl,
        publishTime: article.publishDate ? new Date(article.publishDate).toISOString() : new Date().toISOString(),
        publishDate: article.publishDate
      };
      
      await articlesAPI.updateArticle(id, articleToUpdate);
      alert('✅ Article updated successfully!');
      navigate('/');
    } catch (error) {
      alert('❌ Error updating article: ' + error.message);
    }
  };

  if (!isAuthenticated) {
    return <div className="admin-panel"><p>Redirecting to login...</p></div>;
  }

  if (loading) {
    return <div className="admin-panel"><p>Loading article...</p></div>;
  }

  const categories = ['Film', 'TV', 'Awards', 'Festivals', 'Reviews'];

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>Edit Article</h1>
        <button onClick={() => navigate('/')} className="logout-btn">Cancel</button>
      </div>
      
      <form onSubmit={handleUpdate} className="article-form">
        <div className="form-group">
          <label>Title:</label>
          <input
            type="text"
            value={article.title}
            onChange={(e) => setArticle({...article, title: e.target.value})}
            required
          />
        </div>

        <div className="form-group">
          <label>Subtitle:</label>
          <input
            type="text"
            value={article.subtitle}
            onChange={(e) => setArticle({...article, subtitle: e.target.value})}
          />
        </div>

        <div className="form-group">
          <label>Category:</label>
          <select
            value={article.category}
            onChange={(e) => setArticle({...article, category: e.target.value})}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Author:</label>
          <input
            type="text"
            value={article.author}
            onChange={(e) => setArticle({...article, author: e.target.value})}
            required
          />
        </div>

        <div className="form-group">
          <label>Publish Date:</label>
          <input
            type="date"
            value={article.publishDate}
            onChange={(e) => setArticle({...article, publishDate: e.target.value})}
            required
          />
        </div>

        <div className="form-group">
          <label>Article Image:</label>
          <ImageUploader 
            currentImage={article.image}
            onImageUrl={(url) => setArticle({...article, image: url})}
          />
        </div>

        <div className="form-group">
          <label>Content:</label>
          <RichTextEditor
            value={article.content}
            onChange={(content) => setArticle({...article, content})}
            placeholder="Edit your article content..."
          />
        </div>

        <button type="submit" className="submit-btn">Update Article</button>
      </form>
    </div>
  );
}

export default EditArticle;